import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "antd";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { getCountries, getCountryCallingCode } from "react-phone-number-input/input";
import { CountryCode, E164Number, parsePhoneNumber } from "libphonenumber-js";
import { getOptions } from "@companion-core/shared/app/Utils/config";
import { JPInputPhoneProps } from "./jpInputPhone.model";
import "@companion-core/web/src/Assets/Styles/scss/inputphone.scss";

export const JPInputPhone: React.FC<JPInputPhoneProps> = ({
  name,
  value = "",
  required = false,
  label,
  placeholder,
  icon,
  payloadPath = name,
  testID,
  setFieldErrors,
  onValueChange,
  registerValidation,
}: JPInputPhoneProps) => {
  const { i18n } = useTranslation();
  const form = Form.useFormInstance();
  const i18nLanguage = i18n?.languages[0]?.toLocaleLowerCase();
  const { country: defaultCountry, number: defaultPhone } =
    value && isValidPhoneNumber(value)
      ? parsePhoneNumber(value)
      : {
          country: (getOptions()?.formatLocales[i18nLanguage]?.substr(3, 4) ?? "FR") as CountryCode,
          number: undefined,
        };
  const [error, setError] = useState<string>("");
  const [formattedPhoneInput, setFormattedPhoneInput] = useState<E164Number | undefined>(
    defaultPhone
  );
  const formattedPhoneInputRef = useRef(formattedPhoneInput); // Create a ref to store the latest value and avoid stale closure
  const countryLabels = getCountries().reduce(
    (acc, country) => ({ ...acc, [country]: `${country} +${getCountryCallingCode(country)}` }),
    {}
  );

  useEffect(() => {
    registerValidation &&
      registerValidation(name, () => {
        validateInput();
      });
  }, []);

  useEffect(() => {
    formattedPhoneInputRef.current = formattedPhoneInput; // Update ref value
  }, [formattedPhoneInput]);

  useEffect(() => {
    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: formattedPhoneInput,
      });
    }
    if (onValueChange) {
      onValueChange(formattedPhoneInput ?? "");
    }
  }, [formattedPhoneInput, form, payloadPath]);

  const validateInput = () => {
    const inputRefCurrent = formattedPhoneInputRef.current || "";
    const err =
      !inputRefCurrent && required
        ? "common.form.input.error.required"
        : inputRefCurrent && !isValidPhoneNumber(inputRefCurrent)
          ? "common.form.input.validationErrors.isPhoneNumber"
          : "";
    setError(err);
    setFieldErrors && setFieldErrors(err ? [err] : []);
  };

  const inputId = `${name}-phone`;
  const errorId = `${name}-phone-error`;

  return (
    <div data-testid={testID} className="JPInputPhone">
      {label && (
        <InputLabel label={label} icon={icon} htmlFor={inputId} id={`${name}-phone-label`} />
      )}
      <PhoneInput
        id={inputId}
        international={false}
        className={`JPInputPhone__container ${error && "JPInputPhone__container--error"}`}
        labels={countryLabels}
        placeholder={placeholder}
        limitMaxLength={true}
        flagComponent={() => <></>} // Remove country flags
        internationalIcon={() => <></>} // Remove international icon
        defaultCountry={defaultCountry}
        value={formattedPhoneInput}
        onChange={setFormattedPhoneInput}
        onBlur={validateInput}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      />
      {error && <JPInputError inputLabel={label} testID={testID} error={error} id={errorId} />}
    </div>
  );
};
